import { Form, Formik } from "formik"
import { Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import * as Yup from "yup"

import { MAX_ORDER_COMMENT_LENGTH } from "../../../../../../../../constants/inputValidationDefaults"
import { StandardDialog } from "../../../../../../../../shared/components/dialogs/StandardDialog"
import {
    FormikDateTimePicker,
    FormikSubmitButton,
    FormikTextField,
} from "../../../../../../../../shared/components/forms/Formik"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()(() => ({
    container: {
        padding: "2rem 4rem",
    },
}))

type ClosingTimeFormValues = Readonly<{
    start: DateTime
    end: DateTime
    reason: string
}>

type AddClosingTimeDialogProps = Readonly<{
    onAdd: ({ start, end, reason }: { start?: DateTime; end?: DateTime; reason: string }) => void
    open: boolean
    onClose: () => void
}>

export function AddClosingTimeDialog({ open, onClose, onAdd }: AddClosingTimeDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const initialValues: ClosingTimeFormValues = {
        start: DateTime.now().minus({ days: 1 }),
        end: DateTime.now(),
        reason: "",
    }

    const validationSchema = Yup.object({
        reason: Yup.string().max(
            MAX_ORDER_COMMENT_LENGTH,
            l10n.getString("tab-availability-modal-reason-max", { max: MAX_ORDER_COMMENT_LENGTH }),
        ),
    })

    const handleSubmit = (values: ClosingTimeFormValues) => {
        onAdd({
            start: values.start,
            end: values.end,
            reason: values.reason,
        })
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="tab-availability-modal-title">
                    <>Add closed period</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <Formik<ClosingTimeFormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                <Form>
                    <Stack className={classes.container} spacing="2rem">
                        <Stack direction="row" spacing="1rem">
                            <LocalizedStrict id="tab-availability-side-start-time-label" attrs={{ label: true }}>
                                <FormikDateTimePicker<ClosingTimeFormValues, DateTime>
                                    name="start"
                                    range="all"
                                    label="Start"
                                    ampm={false}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict id="tab-availability-side-end-time-label" attrs={{ label: true }}>
                                <FormikDateTimePicker<ClosingTimeFormValues, DateTime>
                                    name="end"
                                    range="all"
                                    label="End"
                                    ampm={false}
                                />
                            </LocalizedStrict>
                        </Stack>
                        <LocalizedStrict id="tab-availability-modal-reason-label" attrs={{ label: true }}>
                            <FormikTextField<ClosingTimeFormValues> name="reason" label="Reason" fullWidth />
                        </LocalizedStrict>
                        <Stack direction="row" justifyContent="center">
                            <LocalizedStrict id="tab-availability-modal-add-button">
                                <FormikSubmitButton fullWidth={false}>Add</FormikSubmitButton>
                            </LocalizedStrict>
                        </Stack>
                    </Stack>
                </Form>
            </Formik>
        </StandardDialog>
    )
}
